// src/components/subscriptions/RefundRequest.js
import React, { useState, useEffect } from 'react';
import refundService from '../../services/refundService';
import './RefundRequest.css';

const RefundRequest = ({ subscription, onClose, onRefundRequested }) => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [refundCalc, setRefundCalc] = useState(null);
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        fetchRefundCalculation();
    }, []);

    const fetchRefundCalculation = async () => {
        try {
            setLoading(true);
            setError('');
            const calc = await refundService.calculateRefund();
            setRefundCalc(calc);
        } catch (err) {
            setError(err.message || 'Failed to calculate refund');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!reason.trim()) {
            setError('Please provide a reason for the refund request');
            return;
        }

        if (!refundCalc || !refundCalc.refundable) {
            setError('This subscription is not eligible for a refund');
            return;
        }

        try {
            setSubmitting(true);
            setError('');
            const result = await refundService.requestRefund(reason);
            alert('Refund request submitted successfully! Our team will review it shortly.');
            if (onRefundRequested) onRefundRequested(result);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to submit refund request');
        } finally {
            setSubmitting(false);
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            // Some currencies (e.g. PKR) default to 0 fraction digits in Intl's
            // CLDR data even though this app always bills in 2 decimal places -
            // force it explicitly so the figure isn't silently rounded.
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    if (loading) {
        return (
            <div className="modal-overlay" onClick={onClose}>
                <div className="refund-modal" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Request Refund</h2>
                        <button className="close-btn" onClick={onClose}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    <div className="modal-body">
                        <div className="loading-state">
                            <i className="fas fa-spinner fa-spin"></i>
                            <p>Calculating refund amount...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="refund-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Request Refund</h2>
                    <button className="close-btn" onClick={onClose}>
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="modal-body">
                    {error && (
                        <div className="alert alert-error">
                            <i className="fas fa-exclamation-circle"></i>
                            <span>{error}</span>
                        </div>
                    )}

                    {!refundCalc || !refundCalc.refundable ? (
                        <div className="not-refundable">
                            <div className="icon-wrapper">
                                <i className="fas fa-info-circle"></i>
                            </div>
                            <h3>Not Eligible for Refund</h3>
                            <p>{refundCalc?.message || 'This subscription is not eligible for a refund. Only advance payments for upcoming months can be refunded.'}</p>
                            <div className="info-box">
                                <h4>Refund Policy:</h4>
                                <ul>
                                    <li><i className="fas fa-check"></i> Current month payment is non-refundable</li>
                                    <li><i className="fas fa-check"></i> Advance payments can be refunded with 10% deduction</li>
                                    <li><i className="fas fa-check"></i> Refunds are processed within 5-7 business days</li>
                                </ul>
                            </div>
                            <button className="btn btn-secondary" onClick={onClose}>
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            <div className="refund-details">
                                <div className="detail-card">
                                    <div className="detail-header">
                                        <i className="fas fa-calculator"></i>
                                        <h3>Refund Calculation</h3>
                                    </div>
                                    <div className="detail-body">
                                        <div className="detail-row">
                                            <span>Total Subscription:</span>
                                            <strong>{refundCalc.total_months} Months</strong>
                                        </div>
                                        <div className="detail-row">
                                            <span>Months Elapsed:</span>
                                            <strong>{refundCalc.months_elapsed} Month(s)</strong>
                                        </div>
                                        <div className="detail-row highlight">
                                            <span>Months Refundable:</span>
                                            <strong>{refundCalc.months_refunded} Month(s)</strong>
                                        </div>
                                        <div className="detail-row">
                                            <span>Monthly Rate:</span>
                                            <strong>{formatCurrency(refundCalc.monthly_rate, refundCalc.currency)}</strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="detail-card">
                                    <div className="detail-header">
                                        <i className="fas fa-money-bill-wave"></i>
                                        <h3>Refund Breakdown</h3>
                                    </div>
                                    <div className="detail-body">
                                        <div className="detail-row">
                                            <span>Original Amount:</span>
                                            <strong>{formatCurrency(refundCalc.original_amount, refundCalc.currency)}</strong>
                                        </div>
                                        <div className="detail-row deduction">
                                            <span>Processing Fee ({refundCalc.fee_percentage}%):</span>
                                            <strong>- {formatCurrency(refundCalc.fee_amount, refundCalc.currency)}</strong>
                                        </div>
                                        {refundCalc.tax_returned_amount > 0 && (
                                            <div className="detail-row">
                                                <span>Tax Returned ({refundCalc.tax_percentage}%):</span>
                                                <strong>+ {formatCurrency(refundCalc.tax_returned_amount, refundCalc.currency)}</strong>
                                            </div>
                                        )}
                                        <div className="detail-row total">
                                            <span>You Will Receive:</span>
                                            <strong className="refund-amount">
                                                {formatCurrency(refundCalc.refund_amount, refundCalc.currency)}
                                            </strong>
                                        </div>
                                    </div>
                                </div>

                                <div className="info-notice">
                                    <i className="fas fa-info-circle"></i>
                                    <div>
                                        <strong>Important:</strong> The current month's payment is non-refundable. Only advance payments for future months will be refunded after a 10% processing fee deduction.
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label htmlFor="reason">
                                    Reason for Refund <span className="required">*</span>
                                </label>
                                <textarea
                                    id="reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Please provide a reason for your refund request..."
                                    rows="4"
                                    required
                                    disabled={submitting}
                                />
                                <small>This information will help us improve our service.</small>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submitting || !reason.trim()}
                                >
                                    {submitting ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i>
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-paper-plane"></i>
                                            Submit Refund Request
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RefundRequest;
